import { WARNING_COLORS } from '@src/shared/lib/warnings';
import LinkButton from '@src/shared/ui/LinkButton';
import { Box } from '@src/shared/ui/layout';
import {
  GutterWarningIndicator,
  WarningMessageList,
} from '@src/shared/ui/WarningIndicator';
import { useNavigate } from '@tanstack/react-router';
import type { ReactNode, SyntheticEvent } from 'react';
import { absenceTaskReference } from '../lib/taskReference';
import { RESOURCE_GUTTER_WIDTH } from '../renderer/theme';
import type {
  AbsenceWarningItem,
  AbsenceWarningSummary,
  AbsenceWarningTarget,
} from '../types';

const StyleSheet = {
  gutter: {
    bottom: 0,
    cursor: 'default',
    left: 0,
    position: 'absolute',
    top: 0,
    width: `${RESOURCE_GUTTER_WIDTH}px`,
    zIndex: 1,
  },
  // Inside the table: just past the 1px frame drawn at the gutter's end.
  edge: {
    bottom: 0,
    left: `${RESOURCE_GUTTER_WIDTH + 1}px`,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    width: '2px',
    zIndex: 1,
  },
  link: {
    textAlign: 'left',
  },
} as const;

/**
 * A link to the absence a warning is about. The absence is often outside the
 * days on screen, so it opens the way a task reference does, which brings it
 * into view and selects it (see `useAbsenceRouteFocus`).
 */
function WarningLink({
  children,
  target,
}: {
  children: ReactNode;
  target: AbsenceWarningTarget;
}) {
  const navigate = useNavigate();
  const route = absenceTaskReference(target);
  if (!route?.search) {
    return children;
  }

  return (
    <LinkButton
      color="inherit"
      underline="hover"
      sx={StyleSheet.link}
      onClick={() => void navigate(route)}
    >
      {children}
    </LinkButton>
  );
}

function renderWarningMessage({ message, target }: AbsenceWarningItem) {
  return target ? (
    <WarningLink target={target}>{message}</WarningLink>
  ) : (
    message
  );
}

// The gutter looks like it is outside the table, so a click there must not
// reach the row or group it sits beside.
function stopEvent(event: SyntheticEvent) {
  event.stopPropagation();
}

/**
 * A row's warning in the gutter left of the resource column, with its
 * coloured edge inside the table. Positioned against the nearest positioned
 * ancestor, which must be the cell (see `absenceCalendarStyles`).
 */
export default function ResourceWarningGutter({
  summary,
}: {
  summary: AbsenceWarningSummary | null | undefined;
}) {
  if (!summary?.level) {
    return null;
  }

  return (
    <>
      <Box sx={StyleSheet.gutter} onClick={stopEvent} onMouseDown={stopEvent}>
        <GutterWarningIndicator
          edge={false}
          warning={summary.level}
          message={
            <WarningMessageList
              warnings={summary.warnings}
              renderMessage={renderWarningMessage}
            />
          }
          ariaLabel={summary.warnings
            .map((warning) => warning.message)
            .join('. ')}
        />
      </Box>
      <Box
        aria-hidden
        sx={StyleSheet.edge}
        style={{ backgroundColor: WARNING_COLORS[summary.level] }}
      />
    </>
  );
}
